import { useContext, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ContentApp from '../../../components/ContentApp';
import ContentBottom from '../../../components/ContentBottom';
import { CircleSpinner } from 'react-spinners-kit';
import { WelletContext } from '../../../context/WelletContext';
import { getQueryString } from '../../../helpers/url';
import { fetchReservation } from '../../../helpers/reservation';
import { setCanRefresh } from '../../../actions/appActions';
import {
  setVoucherCode,
  resetVoucherCode,
  setSellerDiscount,
} from '../../../actions/reservationActions';

import Summary from '../../../components/ReservationCheck/Summary/Summary';
import SummaryProducts from '../../../components/ReservationCheck/Summary/SummaryProducts';
import SummaryTransfer from '../../../components/ReservationCheck/Summary/SummaryTransfer';
import VoucherRedemption from '../../../components/VoucherRedemption/VoucherRedemption';
import AffiliateDiscount from '../../../components/ReservationCheck/AffiliteDiscount/AffiliateDiscount';
import EventHeader from '../../../components/Common/EventHeader/EventHeader';
import Header from '../../../components/Common/Header/Header';
import Loading from '../../../components/Loading/Loading';
import Commission from '../../../components/ReservationCheck/Commission/Commission';

import Box from '../../../components/Box/Box';
import InputPill from '../../../components/Common/InputPill/InputPill';

const FormCheckTransfer = ({ history }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const welletContext = useContext(WelletContext);

  const reservation = useSelector((state) => state.reservation);
  const countryCode = useSelector((state) => state.app.currentMarket.countryCode);
  const language = useSelector((state) => state.app.language);
  const appCanRefresh = useSelector((state) => state.app.canRefresh);
  const currency = useSelector((state) => state.currency.selected);

  const idReservation = getQueryString('id');
  const transfer = reservation.transfer;

  const [reservationInfo, setReservationInfo] = useState(null);
  const [loadingReservation, setLoadingReservation] = useState(true);
  const [processingReservation, setProcessingReservation] = useState(false);
  const [comment, setComment] = useState('');
  const [voucherStatus, setVoucherStatus] = useState(null);
  const [voucherErrorCode, setVoucherErrorCode] = useState(null);
  const [discountCode, setDiscountCode] = useState(null);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!appCanRefresh) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    dispatch(setCanRefresh(false));
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      dispatch(setCanRefresh(true));
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dispatch, appCanRefresh]);

  const getReservation = async (voucherCode = null) => {
    try {
      const reservationTransferRequest = {
        fromZoneId: transfer.fromZoneId,
        toZoneId: transfer.toZoneId,
        transferType: transfer.transferType,
        trips: transfer.trips.map((trip) => ({
          fromPickupId: trip.fromPickupId,
          toPickupId: trip.toPickupId,
          transferDate: trip.transferDate,
          airline: trip.airline,
          flightNumber: trip.flightNumber,
        })),
      };

      const result = await fetchReservation({
        welletContext,
        reservation: {
          ...reservation,
          date: transfer.trips[0]?.transferDate,
        },
        refReservationId: idReservation,
        reservationTransferRequest,
        paxs: reservation.paxs,
        customerCurrency: currency.shortForm,
        voucherCode,
        sellerDiscount: reservation.sellerDiscount ?? null,
      });

      if (!result.success) {
        throw new Error(result.errorMessage);
      }

      setReservationInfo(result.data);
      return result.data;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoadingReservation(false);
    }
  };
  useEffect(() => {
    getReservation();
  }, []);

  useEffect(() => {
    if (reservation.sellerDiscount !== null || discountCode !== null) {
      getReservation(discountCode);
    }
  }, [reservation.sellerDiscount, discountCode]);

  const onVoucherApply = async (code) => {
    setVoucherStatus('VALIDATING');
    setVoucherErrorCode(null);

    try {
      await getReservation(code);
      setDiscountCode(code);
      dispatch(setVoucherCode(code));
      setVoucherStatus('VALID_COUPON');
    } catch (error) {
      console.error('Voucher error:', error);
      if (error.response?.data?.errorType === 'PROMOTION_ERROR') {
        setVoucherErrorCode(error.response.data.errorMessage);
      } else {
        setVoucherErrorCode(error.message);
      }
      setVoucherStatus(null);
    }
  };

  const onVoucherRemove = async () => {
    setDiscountCode(null);
    setVoucherStatus(null);
    setVoucherErrorCode(null);
    dispatch(resetVoucherCode());
    await getReservation();
  };

  const onApplyDiscount = (discount) => {
    dispatch(setSellerDiscount(discount));
  };

  const renderAffiliateDiscounts = () => {
    if (!reservationInfo?.availableDiscounts?.length) return null;

    return (
      <div className='mt-3'>
        <AffiliateDiscount
          discounts={reservationInfo.availableDiscounts}
          onApplyDiscount={onApplyDiscount}
        />
      </div>
    );
  };

  const sendForm = async () => {
    setProcessingReservation(true);

    try {
      let url = '/reservation/GenerateReservation';
      if (reservationInfo && idReservation) {
        url = '/reservation/modifyreservation';
      }

      const reservationTransferRequest = {
        fromZoneId: transfer.fromZoneId,
        toZoneId: transfer.toZoneId,
        transferType: transfer.transferType,
        trips: transfer.trips.map((trip) => ({
          fromPickupId: trip.fromPickupId,
          toPickupId: trip.toPickupId,
          transferDate: trip.transferDate,
          airline: trip.airline,
          flightNumber: trip.flightNumber,
        })),
      };

      const result = await welletContext.apis.tickets.put(url, {
        showId: reservation.showId,
        paxs: reservation.paxs,
        language: language ?? 'es',
        customer: null,
        comments: comment,
        occasion: null,
        countryCode: countryCode,
        reservationDateLocalTime: transfer.trips[0].transferDate,
        totalAmount:
          reservationInfo?.payment?.remainingDepositAmountInMarketCurrency ??
          reservationInfo?.totalAmount ??
          null,
        products: reservation.products ?? null,
        oldReservationId: idReservation ?? null,
        eventId: reservation.eventId ?? null,
        voucherCode: discountCode ?? null,
        sellerDiscount: reservation.sellerDiscount ?? null,

        customerCurrency: currency.shortForm,
        // externalRefCode:  reservation.refReservationId ?? null,
        customFields: reservation.customFields ?? null,

        reservationTransferRequest,
      });

      history.push({
        pathname: '/reservation-transfer-success',
        state: {
          paymentLink: result.data.paymentLink,
          paymentExpirationDate: result.data.paymentExpirationDate,
          showInfo: {
            bannerImageUrl: reservationInfo?.show.bannerImageUrl,
            name: reservationInfo?.show.name,
            cityName: reservationInfo?.show.city.name,
          },
        },
      });
    } catch (error) {
      console.error(error);
    } finally {
      setProcessingReservation(false);
    }
  };

  if (loadingReservation) {
    return (
      <div className='loaderDiv'>
        <CircleSpinner size={40} color='var(--color-font-primary)' />
      </div>
    );
  }

  if (!reservationInfo) {
    return (
      <div className='loaderDiv'>
        <CircleSpinner size={40} color='var(--color-font-primary)' />
      </div>
    );
  }

  return (
    <>
      <Header />
      <ContentApp classes='fade-up'>
        <EventHeader
          eventImg={reservationInfo?.show?.bannerImageUrl}
          eventName={reservationInfo?.show?.name}
          eventDescription={reservationInfo?.show?.city?.name}
          size='small'
        />

        <div className='mt-4'>
          {reservationInfo.show.reservationType}
          <SummaryProducts reservationInfo={reservationInfo} />
        </div>

        <div className='mt-2'>
          <SummaryTransfer transfers={transfer.trips} lng={language} />
        </div>

        <div className='mt-4'>
          {renderAffiliateDiscounts()}
          <VoucherRedemption
            onApplyVoucher={(e) => onVoucherApply(e)}
            onRemoveVoucher={() => onVoucherRemove()}
            status={voucherStatus}
            errorCode={voucherErrorCode}
          />
        </div>

        <div className={`border-none mt-4`}>
          <Summary reservation={reservationInfo} />
        </div>

        {reservationInfo.commission && reservationInfo.commission.subtotal > 0 && (
          <div className='mt-4'>
            <Commission
              subtotal={reservationInfo.commission.subtotal}
              percentage={reservationInfo.commission.percentage}
              discount={reservationInfo.commission.discountAmount}
              discountPercentage={reservationInfo.commission.discountPercentage}
              total={reservationInfo.commission.total}
            />
          </div>
        )}

        <div className='mt-4'>
          <div className='section-title'>{t('comments')}</div>
          <Box>
            <div className='text-secondary font-tiny'>{t('commentsDescription')}</div>
            <div className='text-secondary font-extra-small mt-3'>
              <InputPill
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('commentsPlaceholder')}
                className='w-100'
                autoCapitalize='sentences'
                autoCorrect='on'
                spellCheck
              />
            </div>
          </Box>
        </div>
      </ContentApp>

      <ContentBottom>
        <div>
          <button className='btn btn-primary w-100' onClick={sendForm}>
            {t('generatePaymentLink')}
          </button>
          <div className='mt-2'>
            <button className='btn btn-secondary w-100' onClick={sendForm}>
              {t('showQRToCollectInPerson')}
            </button>
          </div>
        </div>
      </ContentBottom>

      <Loading show={processingReservation} text={t('generatingPaymentLink')} />
    </>
  );
};

export default FormCheckTransfer;
